import React, { useState, useEffect, useRef } from "react";
import editIcon from "../../../../assets/img/edit-btn.svg";
import deleteIcon from "../../../../assets/img/del-bin.svg";
import editorTItle from "../../../../assets/img/editor-icon.svg";
import "./checklist.css";
import Draggable from "react-draggable";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import Portal from "../../../common/components/Portal";
import ReactTooltip from "react-tooltip";
import { uid } from "../../../../config/utils";

export const RecommendationChecklist = ({ data = [], setData, readOnly = false }) => {
    const [viewMode, setViewMode] = useState(() => (Array.isArray(data) && data.length > 0) || !!readOnly);

    const [expandedParties, setExpandedParties] = useState(() => Object.fromEntries((data || []).map(p => [p.id, true])));
    const [expandedSubs, setExpandedSubs] = useState(() => {
        const map = {};
        (data || []).forEach(p => {
            (p.subBands || []).forEach(sb => {
                map[sb.id] = true;
            });
        });
        return map;
    });

    const firstLoad = useRef(true);

    useEffect(() => {
        setExpandedParties(prev => {
            const next = {};
            (data || []).forEach(p => {
                next[p.id] = prev[p.id] !== undefined ? prev[p.id] : true;
            });
            return next;
        });

        setExpandedSubs(prev => {
            const next = { ...prev };
            (data || []).forEach(p => {
                (p.subBands || []).forEach(sb => {
                    next[sb.id] = prev[sb.id] !== undefined ? prev[sb.id] : true;
                });
            });
            return next;
        });

        if (firstLoad.current) {
            setViewMode((Array.isArray(data) && data.length > 0) || !!readOnly);
            firstLoad.current = false;
        }
    }, [data, readOnly]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [viewMode, expandedParties, expandedSubs]);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("create");
    const [modalTarget, setModalTarget] = useState(null);
    const [modalName, setModalName] = useState("");
    const [modalDescription, setModalDescription] = useState("");

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmPayload, setConfirmPayload] = useState(null);

    function setDataAndNotify(updater) {
        if (typeof setData !== "function") {
            console.warn("RecommendationChecklist: setData prop not provided");
            return;
        }

        if (typeof updater === "function") {
            const next = updater(data);
            setData(next);
        } else {
            setData(updater);
        }
    }

    function toggleParty(id) {
        setExpandedParties(s => ({ ...s, [id]: !s[id] }));
    }
    function toggleSub(id) {
        setExpandedSubs(s => ({ ...s, [id]: !s[id] }));
    }

    function getPartyObligation(party) {
        if (!party) return "";
        if (party._obligation) return party._obligation;
        for (const c of party.checklists || []) if (c.obligation) return c.obligation;
        for (const s of party.subBands || []) for (const c of s.checklists || []) if (c.obligation) return c.obligation;
        return "";
    }

    function updateChecklist(partyId, subId, checklistId, patch) {
        setDataAndNotify(d =>
            d.map(p => {
                if (p.id !== partyId) return p;
                if (subId) {
                    return {
                        ...p,
                        subBands: p.subBands.map(s =>
                            s.id === subId ? { ...s, checklists: s.checklists.map(c => (c.id === checklistId ? { ...c, ...patch } : c)) } : s
                        )
                    };
                }
                return { ...p, checklists: p.checklists.map(c => (c.id === checklistId ? { ...c, ...patch } : c)) };
            })
        );
    }

    function openCreateModal(target) {
        setModalMode("create");
        setModalTarget(target);
        setModalName("");
        setModalDescription("");
        setModalOpen(true);
    }

    function openEditModal(target) {
        setModalMode("edit");
        setModalTarget(target);

        if (target.type === "party") {
            const p = data.find(x => x.id === target.partyId);
            setModalName(p ? p.title : "");
            setModalDescription(p ? p._obligation || getPartyObligation(p) || "" : "");
        } else if (target.type === "subband") {
            const p = data.find(x => x.id === target.partyId);
            const s = p?.subBands?.find(sb => sb.id === target.subId);
            setModalName(s ? s.title : "");
            setModalDescription("");
        } else if (target.type === "checklist") {
            const p = data.find(x => x.id === target.partyId);
            let c;
            if (target.subId) c = p?.subBands?.find(sb => sb.id === target.subId)?.checklists?.find(ch => ch.id === target.checklistId);
            else c = p?.checklists?.find(ch => ch.id === target.checklistId);
            setModalName(c ? c.text : "");
            setModalDescription(c ? c.description || "" : "");
        }

        setModalOpen(true);
    }

    function closeModal() {
        setModalOpen(false);
        setModalTarget(null);
        setModalName("");
        setModalDescription("");
    }

    function saveModal() {
        if (!modalTarget) return;
        const { type, partyId, subId, checklistId } = modalTarget;

        if (modalMode === "create") {
            if (type === "party") {
                const p = { id: uid("party"), title: modalName || "Untitled Party", subBands: [], checklists: [] };
                p._obligation = modalDescription || "";
                setDataAndNotify(prev => [...prev, p]);
                setExpandedParties(s => ({ ...s, [p.id]: true }));
            } else if (type === "subband") {
                const sb = { id: uid("sub"), title: modalName || "Untitled Sub-band", checklists: [] };
                setDataAndNotify(prev => prev.map(p => (p.id === partyId ? { ...p, subBands: [...(p.subBands || []), sb] } : p)));
                setExpandedSubs(s => ({ ...s, [sb.id]: true }));
            } else if (type === "checklist") {
                const party = data.find(p => p.id === partyId);
                const partyTitle = party ? party.title : "";

                const obligation = party ? party._obligation || getPartyObligation(party) || "" : "";

                const groupTitle = subId ? party?.subBands?.find(sb => sb.id === subId)?.title || "" : "";

                const providedId = modalTarget?.providedId;
                const newId = providedId || uid("chk");

                const item = {
                    id: newId,
                    party: partyTitle,
                    obligation,
                    group: groupTitle,
                    text: modalName || "Untitled checklist",
                    value: "",
                    notes: "",
                    description: modalDescription || "",
                    rules: ""
                };

                if (subId) {
                    setDataAndNotify(prev =>
                        prev.map(p =>
                            p.id === partyId
                                ? {
                                      ...p,
                                      subBands: p.subBands.map(s => (s.id === subId ? { ...s, checklists: [...(s.checklists || []), item] } : s))
                                  }
                                : p
                        )
                    );
                } else {
                    setDataAndNotify(prev => prev.map(p => (p.id === partyId ? { ...p, checklists: [...(p.checklists || []), item] } : p)));
                }
            }
        } else {
            if (type === "party") {
                setDataAndNotify(prev => prev.map(p => (p.id === partyId ? { ...p, title: modalName, _obligation: modalDescription } : p)));
            } else if (type === "subband") {
                setDataAndNotify(prev =>
                    prev.map(p => (p.id === partyId ? { ...p, subBands: p.subBands.map(s => (s.id === subId ? { ...s, title: modalName } : s)) } : p))
                );
            } else if (type === "checklist") {
                setDataAndNotify(prev =>
                    prev.map(p => {
                        if (p.id !== partyId) return p;
                        if (subId) {
                            return {
                                ...p,
                                subBands: p.subBands.map(s =>
                                    s.id === subId
                                        ? {
                                              ...s,
                                              checklists: s.checklists.map(c =>
                                                  c.id === checklistId ? { ...c, text: modalName, description: modalDescription } : c
                                              )
                                          }
                                        : s
                                )
                            };
                        }
                        return {
                            ...p,
                            checklists: p.checklists.map(c => (c.id === checklistId ? { ...c, text: modalName, description: modalDescription } : c))
                        };
                    })
                );
            }
        }

        closeModal();
    }

    function openConfirm(actionPayload) {
        setConfirmPayload(actionPayload);
        setConfirmOpen(true);
    }
    function closeConfirm() {
        setConfirmOpen(false);
        setConfirmPayload(null);
    }

    function confirmYes() {
        if (!confirmPayload) return closeConfirm();
        const { action, partyId, subId, checklistId } = confirmPayload;
        if (action === "deleteParty") {
            setDataAndNotify(d => d.filter(p => p.id !== partyId));
            setExpandedParties(s => {
                const clone = { ...s };
                delete clone[partyId];
                return clone;
            });
        } else if (action === "deleteSub") {
            setDataAndNotify(d => d.map(p => (p.id === partyId ? { ...p, subBands: p.subBands.filter(s => s.id !== subId) } : p)));
            setExpandedSubs(s => {
                const clone = { ...s };
                delete clone[subId];
                return clone;
            });
        } else if (action === "deleteChecklist") {
            setDataAndNotify(d =>
                d.map(p => {
                    if (p.id !== partyId) return p;
                    if (subId)
                        return {
                            ...p,
                            subBands: p.subBands.map(s => (s.id === subId ? { ...s, checklists: s.checklists.filter(c => c.id !== checklistId) } : s))
                        };
                    return { ...p, checklists: p.checklists.filter(c => c.id !== checklistId) };
                })
            );
        }
        closeConfirm();
    }

    const handleRemoveParty = partyId => openConfirm({ action: "deleteParty", partyId });
    const handleRemoveSubBand = (partyId, subId) => openConfirm({ action: "deleteSub", partyId, subId });
    const handleRemoveChecklist = (partyId, subId, checklistId) => openConfirm({ action: "deleteChecklist", partyId, subId, checklistId });

    const isReadOnly = !!readOnly;
    const isEditMode = !isReadOnly && !viewMode;
    return (
        <div className="checklist-outer-wrp">
            <ReactTooltip id="checklist-info" effect="solid" place="top" backgroundColor="#007bff" />
            <div className="top-info-wrap">
                <div className="top-info-title">Recommendation Checklist</div>
                <div className="action-btn-right">
                    {!isReadOnly && (
                        <>
                            {!viewMode ? (
                                <>
                                    <button type="button" className="back-blue-common-butn add-btn" onClick={() => setViewMode(true)}>
                                        <i className="fas fa-eye" /> View Mode
                                    </button>
                                    <button
                                        type="button"
                                        className="back-blue-common-butn add-btn"
                                        onClick={() => openCreateModal({ type: "party" })}
                                    >
                                        <i className="fas fa-plus" /> Add New Party
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="back-blue-common-butn add-btn" onClick={() => setViewMode(false)}>
                                    <i className="fas fa-edit" /> Edit Mode
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="checklist-collapsible-wrap">
                <div className="accordion checklist-accordion" id="accordion">
                    {data.map(party => (
                        <div className="card checklist-acco-item" key={party.id}>
                            <div className="card-header checklist-acco-header">
                                <div className="inner-border-wrp">
                                    <button
                                        className="btn btn-link"
                                        type="button"
                                        onClick={() => toggleParty(party.id)}
                                        aria-expanded={!!expandedParties[party.id]}
                                    >
                                        <h4 className="acc-title">{party.title}</h4>
                                        <div className="acc-desc">{getPartyObligation(party)}</div>
                                    </button>

                                    <div className="rt-sec">
                                        {/* <button type="button" className="btn btn-items-info">
                                            {party.subBands.length} Sub. {party.checklists.length} Items
                                        </button> */}
                                        <div className="edit-btn-sec">
                                            {isEditMode && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outlined-new"
                                                    onClick={() => openCreateModal({ type: "subband", partyId: party.id })}
                                                >
                                                    <i className="fas fa-plus" /> Sub band
                                                </button>
                                            )}
                                            {isEditMode && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outlined-new"
                                                    onClick={() => openCreateModal({ type: "checklist", partyId: party.id })}
                                                >
                                                    <i className="fas fa-plus" /> Checklist
                                                </button>
                                            )}
                                        </div>

                                        <div className="action-btn-wrp">
                                            {isEditMode && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn action-btn"
                                                        data-for="checklist-info"
                                                        data-tip="Edit"
                                                        onClick={() => openEditModal({ type: "party", partyId: party.id })}
                                                    >
                                                        <img src={editIcon} alt="edit btn" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn action-btn"
                                                        data-for="checklist-info"
                                                        data-tip="Delete"
                                                        onClick={() => handleRemoveParty(party.id)}
                                                    >
                                                        <img src={deleteIcon} alt="delete btn" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {expandedParties[party.id] && (
                                <div className="accordion-collapse collapse show">
                                    <div className="accordion-body checklist-acco-body">
                                        {party.checklists.length > 0 && (
                                            <div className="chklist-acco-body-inner">
                                                {/* party-level checklists */}
                                                {party.checklists &&
                                                    party.checklists.map(c => (
                                                        <div className="editor-wrp" key={c.id}>
                                                            <div className="top-info-sec">
                                                                <div className="title">
                                                                    <span className="icon">
                                                                        <img src={editorTItle} alt="" />
                                                                    </span>
                                                                    {c.text || <em>— no title —</em>}
                                                                    {c.description && (
                                                                        <i
                                                                            className="fa fa-info"
                                                                            data-for="checklist-info"
                                                                            data-tip={c.description}
                                                                        />
                                                                    )}
                                                                </div>
                                                                <div className="top-rt">
                                                                    {viewMode ? (
                                                                        <>
                                                                            <div className={`form-check${isReadOnly ? " cursor-disabled" : ""}`}>
                                                                                <input
                                                                                    id={`yes_${c.id}`}
                                                                                    className={`form-check-input${
                                                                                        isReadOnly ? " cursor-disabled" : ""
                                                                                    }`}
                                                                                    type="checkbox"
                                                                                    name={`r_${c.id}`}
                                                                                    checked={c.value === "Yes"}
                                                                                    onChange={() =>
                                                                                        !isReadOnly &&
                                                                                        updateChecklist(party.id, null, c.id, {
                                                                                            value: c?.value === "Yes" ? "" : "Yes"
                                                                                        })
                                                                                    }
                                                                                />
                                                                                <label className="form-check-label" htmlFor={`yes_${c.id}`}>
                                                                                    Yes
                                                                                </label>
                                                                            </div>
                                                                            <div className={`form-check${isReadOnly ? " cursor-disabled" : ""}`}>
                                                                                <input
                                                                                    id={`no_${c.id}`}
                                                                                    className={`form-check-input${
                                                                                        isReadOnly ? " cursor-disabled" : ""
                                                                                    }`}
                                                                                    type="checkbox"
                                                                                    name={`r_${c.id}`}
                                                                                    checked={c.value === "No"}
                                                                                    onChange={() =>
                                                                                        !isReadOnly &&
                                                                                        updateChecklist(party.id, null, c.id, {
                                                                                            value: c?.value === "No" ? "" : "No"
                                                                                        })
                                                                                    }
                                                                                />
                                                                                <label className="form-check-label" htmlFor={`no_${c.id}`}>
                                                                                    No
                                                                                </label>
                                                                            </div>
                                                                            <div className={`form-check${isReadOnly ? " cursor-disabled" : ""}`}>
                                                                                <input
                                                                                    id={`na_${c.id}`}
                                                                                    className={`form-check-input${
                                                                                        isReadOnly ? " cursor-disabled" : ""
                                                                                    }`}
                                                                                    type="checkbox"
                                                                                    name={`r_${c.id}`}
                                                                                    checked={c.value === "NA"}
                                                                                    onChange={() =>
                                                                                        !isReadOnly &&
                                                                                        updateChecklist(party.id, null, c.id, {
                                                                                            value: c?.value === "NA" ? "" : "NA"
                                                                                        })
                                                                                    }
                                                                                />
                                                                                <label className="form-check-label" htmlFor={`na_${c.id}`}>
                                                                                    NA
                                                                                </label>
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-close-editor"
                                                                                data-for="checklist-info"
                                                                                data-tip="Edit"
                                                                                onClick={() =>
                                                                                    openEditModal({
                                                                                        type: "checklist",
                                                                                        partyId: party.id,
                                                                                        checklistId: c.id
                                                                                    })
                                                                                }
                                                                            >
                                                                                <img src={editIcon} alt="edit checklist" />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                data-for="checklist-info"
                                                                                data-tip="Delete"
                                                                                className="btn btn-close-editor"
                                                                                onClick={() => handleRemoveChecklist(party.id, null, c.id)}
                                                                            >
                                                                                <img src={deleteIcon} alt="" />
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {viewMode && (
                                                                <div className="editor-area-sec">
                                                                    {/* <div className="text-label">Description:</div>
                                                            {!viewMode ? (
                                                                <textarea
                                                                    value={c.description}
                                                                    placeholder="Description"
                                                                    onChange={e =>
                                                                        updateChecklist(party.id, null, c.id, { description: e.target.value })
                                                                    }
                                                                />
                                                            ) : (
                                                                <div style={{ padding: "6px 0", color: "#6b7280" }}>
                                                                    {c.description || <em className="muted">— no description —</em>}
                                                                </div>
                                                            )} */}

                                                                    <div>
                                                                        <div className="text-label">Note:</div>
                                                                        {isReadOnly ? (
                                                                            <div style={{ padding: "6px 0", color: "#6b7280" }}>
                                                                                {c.notes || <em className="muted">— no note —</em>}
                                                                            </div>
                                                                        ) : (
                                                                            <textarea
                                                                                value={c.notes}
                                                                                placeholder="Add a note"
                                                                                onChange={e =>
                                                                                    updateChecklist(party.id, null, c.id, { notes: e.target.value })
                                                                                }
                                                                                style={{ width: "100%" }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                        {/* sub-bands wrapped */}
                                        <div className="subband-list-wrp">
                                            {party.subBands &&
                                                party.subBands.map(s => (
                                                    <div
                                                        className="card checklist-acco-item"
                                                        key={s.id}
                                                        style={{ border: "none", boxShadow: "none", marginTop: 12 }}
                                                    >
                                                        <div className="card-header checklist-acco-header" style={{ padding: 0 }}>
                                                            <div className="inner-border-wrp">
                                                                <button
                                                                    className="btn btn-link"
                                                                    type="button"
                                                                    onClick={() => toggleSub(s.id)}
                                                                    aria-expanded={!!expandedSubs[s.id]}
                                                                >
                                                                    <h4 className="acc-title">{s.title}</h4>
                                                                </button>

                                                                <div className="rt-sec">
                                                                    {/* <button type="button" className="btn btn-items-info">{s.checklists.length} Items</button> */}
                                                                    <div className="edit-btn-sec">
                                                                        {isEditMode && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outlined-new"
                                                                                onClick={() =>
                                                                                    openCreateModal({
                                                                                        type: "checklist",
                                                                                        partyId: party.id,
                                                                                        subId: s.id
                                                                                    })
                                                                                }
                                                                            >
                                                                                <i className="fas fa-plus" /> Checklist
                                                                            </button>
                                                                        )}
                                                                    </div>

                                                                    <div className="action-btn-wrp">
                                                                        {isEditMode && (
                                                                            <>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn action-btn"
                                                                                    data-for="checklist-info"
                                                                                    data-tip="Edit"
                                                                                    onClick={() =>
                                                                                        openEditModal({
                                                                                            type: "subband",
                                                                                            partyId: party.id,
                                                                                            subId: s.id
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    <img src={editIcon} alt="edit btn" />
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn action-btn"
                                                                                    data-for="checklist-info"
                                                                                    data-tip="Delete"
                                                                                    onClick={() => handleRemoveSubBand(party.id, s.id)}
                                                                                >
                                                                                    <img src={deleteIcon} alt="delete btn" />
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {expandedSubs[s.id] && (
                                                            <div className="accordion-collapse collapse show">
                                                                <div className="accordion-body checklist-acco-body">
                                                                    <div className="chklist-acco-body-inner">
                                                                        {s.checklists &&
                                                                            s.checklists.map(c => (
                                                                                <div className="editor-wrp" key={c.id}>
                                                                                    <div className="top-info-sec">
                                                                                        <div className="title">
                                                                                            <span className="icon">
                                                                                                <img src={editorTItle} alt="" />
                                                                                            </span>
                                                                                            {c.text || <em>— no title —</em>}
                                                                                            {c.description && (
                                                                                                <i
                                                                                                    className="fa fa-info"
                                                                                                    data-for="checklist-info"
                                                                                                    data-tip={c.description}
                                                                                                />
                                                                                            )}
                                                                                        </div>
                                                                                        <div className="top-rt">
                                                                                            {viewMode ? (
                                                                                                <>
                                                                                                    <div
                                                                                                        className={`form-check${
                                                                                                            isReadOnly ? " cursor-disabled" : ""
                                                                                                        }`}
                                                                                                    >
                                                                                                        <input
                                                                                                            className={`form-check-input${
                                                                                                                isReadOnly ? " cursor-disabled" : ""
                                                                                                            }`}
                                                                                                            type="checkbox"
                                                                                                            name={`r_${c.id}`}
                                                                                                            id={`yes_${c.id}`}
                                                                                                            checked={c.value === "Yes"}
                                                                                                            onChange={() =>
                                                                                                                !isReadOnly &&
                                                                                                                updateChecklist(
                                                                                                                    party.id,
                                                                                                                    s.id,
                                                                                                                    c.id,
                                                                                                                    {
                                                                                                                        value:
                                                                                                                            c?.value === "Yes"
                                                                                                                                ? ""
                                                                                                                                : "Yes"
                                                                                                                    }
                                                                                                                )
                                                                                                            }
                                                                                                        />
                                                                                                        <label
                                                                                                            className="form-check-label"
                                                                                                            htmlFor={`yes_${c.id}`}
                                                                                                        >
                                                                                                            Yes
                                                                                                        </label>
                                                                                                    </div>
                                                                                                    <div
                                                                                                        className={`form-check${
                                                                                                            isReadOnly ? " cursor-disabled" : ""
                                                                                                        }`}
                                                                                                    >
                                                                                                        <input
                                                                                                            className={`form-check-input${
                                                                                                                isReadOnly ? " cursor-disabled" : ""
                                                                                                            }`}
                                                                                                            type="checkbox"
                                                                                                            name={`r_${c.id}`}
                                                                                                            id={`no_${c.id}`}
                                                                                                            checked={c.value === "No"}
                                                                                                            onChange={() =>
                                                                                                                !isReadOnly &&
                                                                                                                updateChecklist(
                                                                                                                    party.id,
                                                                                                                    s.id,
                                                                                                                    c.id,
                                                                                                                    {
                                                                                                                        value:
                                                                                                                            c?.value === "No"
                                                                                                                                ? ""
                                                                                                                                : "No"
                                                                                                                    }
                                                                                                                )
                                                                                                            }
                                                                                                        />
                                                                                                        <label
                                                                                                            className="form-check-label"
                                                                                                            htmlFor={`no_${c.id}`}
                                                                                                        >
                                                                                                            No
                                                                                                        </label>
                                                                                                    </div>
                                                                                                    <div
                                                                                                        className={`form-check${
                                                                                                            isReadOnly ? " cursor-disabled" : ""
                                                                                                        }`}
                                                                                                    >
                                                                                                        <input
                                                                                                            className={`form-check-input${
                                                                                                                isReadOnly ? " cursor-disabled" : ""
                                                                                                            }`}
                                                                                                            type="checkbox"
                                                                                                            name={`r_${c.id}`}
                                                                                                            id={`na_${c.id}`}
                                                                                                            checked={c.value === "NA"}
                                                                                                            onChange={() =>
                                                                                                                !isReadOnly &&
                                                                                                                updateChecklist(
                                                                                                                    party.id,
                                                                                                                    s.id,
                                                                                                                    c.id,
                                                                                                                    {
                                                                                                                        value:
                                                                                                                            c?.value === "NA"
                                                                                                                                ? ""
                                                                                                                                : "NA"
                                                                                                                    }
                                                                                                                )
                                                                                                            }
                                                                                                        />
                                                                                                        <label
                                                                                                            className="form-check-label"
                                                                                                            htmlFor={`na_${c.id}`}
                                                                                                        >
                                                                                                            NA
                                                                                                        </label>
                                                                                                    </div>
                                                                                                </>
                                                                                            ) : (
                                                                                                <>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-close-editor"
                                                                                                        data-for="checklist-info"
                                                                                                        data-tip="Edit"
                                                                                                        onClick={() =>
                                                                                                            openEditModal({
                                                                                                                type: "checklist",
                                                                                                                partyId: party.id,
                                                                                                                subId: s.id,
                                                                                                                checklistId: c.id
                                                                                                            })
                                                                                                        }
                                                                                                    >
                                                                                                        <img src={editIcon} alt="edit checklist" />
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-close-editor"
                                                                                                        data-for="checklist-info"
                                                                                                        data-tip="Delete"
                                                                                                        onClick={() =>
                                                                                                            handleRemoveChecklist(
                                                                                                                party.id,
                                                                                                                s.id,
                                                                                                                c.id
                                                                                                            )
                                                                                                        }
                                                                                                    >
                                                                                                        <img src={deleteIcon} alt="" />
                                                                                                    </button>
                                                                                                </>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>

                                                                                    {viewMode && (
                                                                                        <div className="editor-area-sec">
                                                                                            {/* <div className="text-label">Description:</div>
                                                                                            {!viewMode ? (
                                                                                                <textarea
                                                                                                    value={c.description}
                                                                                                    placeholder="Description"
                                                                                                    onChange={e =>
                                                                                                        updateChecklist(party.id, s.id, c.id, {
                                                                                                            description: e.target.value
                                                                                                        })
                                                                                                    }
                                                                                                />
                                                                                            ) : (
                                                                                                <div style={{ padding: "6px 0", color: "#6b7280" }}>
                                                                                                    {c.description || (
                                                                                                        <em className="muted">— no description —</em>
                                                                                                    )}
                                                                                                </div>
                                                                                            )} */}

                                                                                            <div>
                                                                                                <div className="text-label">Note:</div>
                                                                                                {isReadOnly ? (
                                                                                                    <div
                                                                                                        style={{ padding: "6px 0", color: "#6b7280" }}
                                                                                                    >
                                                                                                        {c.notes || (
                                                                                                            <em className="muted">— no note —</em>
                                                                                                        )}
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <textarea
                                                                                                        value={c.notes}
                                                                                                        readOnly={isReadOnly}
                                                                                                        placeholder="Add a note"
                                                                                                        onChange={e =>
                                                                                                            updateChecklist(party.id, s.id, c.id, {
                                                                                                                notes: e.target.value
                                                                                                            })
                                                                                                        }
                                                                                                        style={{ width: "100%" }}
                                                                                                    />
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                        </div>
                                        {/* end sub-bands */}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* EditEntityModal via Portal */}
            {modalOpen && (
                <Portal
                    body={
                        <EditEntityModal
                            mode={modalMode}
                            target={modalTarget}
                            name={modalName}
                            description={modalDescription}
                            onNameChange={setModalName}
                            onDescriptionChange={setModalDescription}
                            onSave={saveModal}
                            onCancel={closeModal}
                        />
                    }
                    onCancel={closeModal}
                />
            )}

            {/* ConfirmationModal via Portal (contextual heading & message) */}
            {confirmOpen && (
                <Portal
                    body={
                        <ConfirmationModal
                            heading={
                                confirmPayload
                                    ? confirmPayload.action === "deleteParty"
                                        ? "Are you sure you want to remove this party?"
                                        : confirmPayload.action === "deleteSub"
                                        ? "Are you sure you want to remove this sub-band?"
                                        : confirmPayload.action === "deleteChecklist"
                                        ? "Are you sure you want to remove this checklist?"
                                        : "Are you sure?"
                                    : "Are you sure?"
                            }
                            message={"This action cannot be reverted. Are you sure you want to continue?"}
                            onNo={closeConfirm}
                            onYes={confirmYes}
                            type="cancel"
                        />
                    }
                    onCancel={closeConfirm}
                />
            )}
        </div>
    );
};

/* ---------- EditEntityModal (reusable modal used for create/edit) ---------- */
function EditEntityModal({
    mode = "create", // 'create' | 'edit'
    target = null, // { type: 'party'|'subband'|'checklist', partyId?, subId?, checklistId? }
    name,
    description,
    onNameChange,
    onDescriptionChange,
    onSave,
    onCancel
}) {
    const inputRef = useRef(null);
    const [showError, setShowError] = useState(false);

    useEffect(() => {
        // autofocus
        setTimeout(() => inputRef.current && inputRef.current.focus(), 10);

        function onKey(e) {
            if (e.key === "Escape") {
                onCancel();
            }
            if (e.key === "Enter") {
                // ignore Enter inside textarea
                const active = document.activeElement;
                if (active && active.tagName === "TEXTAREA") return;
                e.preventDefault();
                if (!name || !name.trim()) {
                    setShowError(true);
                    return;
                }
                onSave();
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name]);

    useEffect(() => setShowError(false), [name]);

    const label = target?.type === "checklist" ? "Label" : "Name";
    const titleText = `${mode === "create" ? "Create" : "Edit"} ${target?.type || ""}`;

    return (
        <div className="modal modal-region modal-view inbox-modal" style={{ display: "block" }}>
            <Draggable handle=".draggable" cancel=".non-draggable">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header draggable">
                            <h5 className="modal-title">
                                <div className="txt-hed">{titleText}</div>
                            </h5>
                            <button type="button" className="close non-draggable" onClick={onCancel}>
                                <span aria-hidden="true">
                                    <img src="/img/close.svg" alt="close" />
                                </span>
                            </button>
                        </div>

                        <div className="modal-body region-otr sm-chart-upload">
                            <div className="col-md-12 main-sec">
                                <div className="form-row">
                                    <div className="form-group col-12 mb-4">
                                        <label>{label}</label>
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            placeholder={label}
                                            value={name}
                                            onChange={e => onNameChange(e.target.value)}
                                            className={`form-control ${showError && (!name || !name.trim()) ? "error-border" : ""}`}
                                        />
                                        {showError && (!name || !name.trim()) && (
                                            <div className="form-error" style={{ color: "#dc2626", marginTop: 6 }}>
                                                {label} is required
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {target?.type === "checklist" && (
                                    <div className="form-row">
                                        <div className="form-group col-12 mb-4">
                                            <label>Description</label>
                                            <textarea
                                                className="form-control textarea"
                                                value={description}
                                                onChange={e => onDescriptionChange(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}

                                {target?.type === "party" && (
                                    <div className="form-row">
                                        <div className="form-group col-12 mb-4">
                                            <label>Obligation</label>
                                            <textarea
                                                className="form-control textarea"
                                                value={description}
                                                onChange={e => onDescriptionChange(e.target.value)}
                                                placeholder="Obligation"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="btn-sec">
                                <div className="text-right btnOtr edit-cmn-btn">
                                    <button
                                        type="button"
                                        className="btn btn-primary btnRgion btn-create save"
                                        onClick={() => {
                                            if (!name || !name.trim()) {
                                                setShowError(true);
                                                return;
                                            }
                                            onSave();
                                        }}
                                    >
                                        {mode === "create" ? "Create" : "Save"}
                                    </button>
                                    <button type="button" className="btn btn-secondary btnClr mr-1" onClick={onCancel}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Draggable>
        </div>
    );
}
